import React, { useState, useEffect, useRef } from "react";
import CustomAxios from "../../utils/CustomAxios";
import useStatusToggle from "../../hooks/useStatusToggle";
import axios from "axios";
import { Button, Typography, Box } from "@mui/material";
import { Toast } from "primereact/toast";
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';


const ProfileSettings = () => {
    const [avatars, setAvatars] = useState([]);
    const statusToggle = useStatusToggle();
    const [loading, setLoading] = useState(false);
    const fileInputRef = useRef(null);
    const toast = useRef(null);

    // Fetch avatars from backend
    const fetchAvatars = async () => {
        try {
            const res = await CustomAxios.get("/avatars/");
            setAvatars(res.data);
        } catch (err) {
            console.error("Error fetching avatars:", err);
        }
    };

    useEffect(() => {
        fetchAvatars();
    }, []);

    // Trigger hidden file input
    const handleAddProfileClick = () => {
        fileInputRef.current.click();
    };

    // Handle file selection & upload
    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file size: max 5MB
        const maxSize = 5 * 1024 * 1024; // 5MB in bytes
        if (file.size > maxSize) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'File size should not exceed 5MB', life: 3000 });
            e.target.value = "";
            return;
        }

        // Validate file type: only jpeg/png
        const isValidType =
            file.type === 'image/jpeg' ||
            file.type === 'image/png' ||
            /\.(jpg|jpeg|png)$/i.test(file.name);
        if (!isValidType) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'File type should be jpeg or png', life: 3000 });
            e.target.value = "";
            return;
        }

        setLoading(true);
        try {
            // Step 1: Request SAS URL + stable URL
            const res = await CustomAxios.post("/avatars/upload/", {
                file_name: file.name,
            });
            const uploadUrl = res?.data?.upload_url;
            const stableUrl = res?.data?.stable_url;

            console.log("uploadUrl:", uploadUrl);
            console.log("stableUrl:", stableUrl);


            if (!uploadUrl?.startsWith("http")) {
                throw new Error("Invalid uploadUrl: " + uploadUrl);
            }


            // Step 2: Upload file to Azure
            await axios.put(uploadUrl, file, {
                headers: { "x-ms-blob-type": "BlockBlob" },
            });
            console.log('kee')


            // Step 3: Notify backend that upload is done
            await CustomAxios.post("/avatars/confirm/", {
                file_name: file.name,
                blob_url: stableUrl,
            });

            toast.current.show({ severity: 'success', summary: 'Success', detail: 'Profile uploaded successfully!', life: 3000 });
            fetchAvatars();
        } catch (err) {
            console.error("Upload error:", err);
            toast.current.show({ severity: 'error', summary: 'Error', detail: `Upload failed: ${err?.message || ''}`, life: 3000 });
        } finally {
            setLoading(false);
            e.target.value = ""; // reset input
        }
    };


    // Ask for confirmation for activate/deactivate
    const toggleActive = (avatarId, currentStatus) => {
        const confirmMode = currentStatus ? "deactivate" : "activate";
        const actionText = currentStatus ? "deactivate" : "activate";

        confirmDialog({
            message: (
                <Box sx={{ display: "flex", gap: "10px", flexDirection: "column" }}>
                    <Typography sx={{ fontSize: "16px", fontWeight: 500 }}>
                        Do you want to {actionText} this profile image?
                    </Typography>
                </Box>
            ),
            header: "Confirm",
            defaultFocus: 'reject',
            acceptClassName: confirmMode === "deactivate" ? 'p-button-danger' : 'p-button-success',
            accept: () => confirmToggle(avatarId, confirmMode),
        });
    };

    /**
     * Remove a profile image for good.
     *
     * Distinct from the Active/Inactive toggle beside it, which only hides an
     * image from the picker and leaves it on every account that already chose
     * it. This erases the row and the file behind it.
     *
     * The usage count is fetched BEFORE the dialog, not after the delete, so
     * the admin is told "12 customers are using this" while they can still say
     * no. `profile_image` is SET_NULL, so those accounts lose their picture
     * rather than breaking — but they do lose it, and that is not obvious from
     * a button labelled Delete.
     */
    const deleteAvatar = async (avatar) => {
        let inUse = 0;
        try {
            const res = await CustomAxios.get(`/avatars/${avatar.id}/usage/`);
            inUse = Number(res?.data?.in_use_by || 0);
        } catch (_) {
            // A count we could not fetch must not block the delete; the dialog
            // simply drops the sentence about it.
        }

        confirmDialog({
            message: (
                <Box sx={{ display: "flex", gap: "10px", flexDirection: "column" }}>
                    <Typography sx={{ fontSize: "16px", fontWeight: 500 }}>
                        Delete “{avatar.file_name}” permanently?
                    </Typography>
                    <Typography sx={{ fontSize: "14px", color: "#6E6478" }}>
                        The image file is removed from storage and cannot be recovered.
                    </Typography>
                    {inUse > 0 && (
                        <Typography sx={{ fontSize: "14px", color: "#B4472F", fontWeight: 500 }}>
                            {inUse} customer{inUse === 1 ? "" : "s"} currently use this picture
                            and will be left without one.
                        </Typography>
                    )}
                </Box>
            ),
            header: "Delete profile image",
            defaultFocus: 'reject',
            acceptLabel: 'Delete',
            acceptClassName: 'p-button-danger',
            accept: () => confirmDelete(avatar.id),
        });
    };

    const confirmDelete = async (avatarId) => {
        try {
            const res = await CustomAxios.delete(`/avatars/${avatarId}/`);
            const cleared = Number(res?.data?.cleared_from_accounts || 0);
            toast.current.show({
                severity: 'success',
                summary: 'Deleted',
                detail: cleared
                    ? `Profile image deleted · cleared from ${cleared} account${cleared === 1 ? "" : "s"}`
                    : 'Profile image deleted',
                life: 3000,
            });
            await fetchAvatars();
        } catch (err) {
            console.error("Error deleting avatar:", err);
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to delete profile image', life: 3000 });
        }
    };

    // The guard belongs here rather than on the button: the confirm dialog can
    // be accepted twice as easily as the button can be clicked twice, and only
    // this point is common to both.
    const confirmToggle = async (avatarId, confirmMode) => {
        await statusToggle.run(avatarId, confirmMode !== "deactivate", async () => {
            try {
                if (confirmMode === "deactivate") {
                    await CustomAxios.delete(`/avatars/${avatarId}/delete/`);
                    toast.current.show({ severity: 'success', summary: 'Success', detail: 'Profile deactivated successfully!', life: 3000 });
                } else {
                    await CustomAxios.patch(`/avatars/${avatarId}/`, { is_active: true });
                    toast.current.show({ severity: 'success', summary: 'Success', detail: 'Profile activated successfully!', life: 3000 });
                }
            } catch (err) {
                console.error("Error confirming toggle:", err);
                toast.current.show({ severity: 'error', summary: 'Error', detail: `Failed to ${confirmMode} profile`, life: 3000 });
                throw err;
            }
            await fetchAvatars();
        });
    };

    return (
        <div style={{ padding: "30px" }}>
            <Toast ref={toast} />
            <ConfirmDialog />
            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "20px", marginTop: "15px" }}>
                <Typography sx={{ fontSize: "16px", fontWeight: "700", lineHeight: "24px" }}>Profile Settings</Typography>
                <Typography sx={{ fontSize: "14px", fontWeight: "500", lineHeight: "22px" }}>Profile Details</Typography>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", marginBottom: "20px" }}>
                <Box />
                <Box sx={{ display: "flex", gap: "20px" }}>
                    <Button variant="contained" sx={{ textTransform: "none" }} onClick={handleAddProfileClick} disabled={loading}>
                        <Typography sx={{ fontSize: { sm: "12px", md: "14px" }, fontWeight: "400", lineHeight: "22px", textAlign: "center" }}>
                            {loading ? "Uploading..." : "Add Profile"}
                        </Typography>
                    </Button>
                    <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        onChange={handleFileChange}
                        accept="image/jpeg,image/png"
                    />
                </Box>
            </Box>

            {/* Stats Bar: Total, Active, Inactive */}
            <Box sx={{ height: "94px", width: "100%", backgroundColor: "white", display: "flex", alignItems: "center", mb: 3, borderRadius: "4px" }}>
                <Box sx={{ width: "100%" }}>
                    <Typography sx={{ fontSize: "24px", fontWeight: "500", lineHeight: "32px", textAlign: "center" }}>
                        {avatars.length}
                    </Typography>
                    <Typography sx={{ fontSize: "14px", fontWeight: "400", lineHeight: "22px", textAlign: "center" }}>
                        Total Profiles
                    </Typography>
                </Box>
                <Box sx={{ width: "100%" }}>
                    <Typography sx={{ fontSize: "24px", fontWeight: "500", lineHeight: "32px", textAlign: "center" }}>
                        {avatars.filter(a => a.is_active).length}
                    </Typography>
                    <Typography sx={{ fontSize: "14px", fontWeight: "400", lineHeight: "22px", textAlign: "center" }}>
                        Active Profiles
                    </Typography>
                </Box>
                <Box sx={{ width: "100%" }}>
                    <Typography sx={{ fontSize: "24px", fontWeight: "500", lineHeight: "32px", textAlign: "center" }}>
                        {avatars.length - avatars.filter(a => a.is_active).length}
                    </Typography>
                    <Typography sx={{ fontSize: "14px", fontWeight: "400", lineHeight: "22px", textAlign: "center" }}>
                        Inactive Profiles
                    </Typography>
                </Box>
            </Box>

            {/* Avatars Grid */}
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
                    gap: "20px",
                }}
            >
                {avatars.map((avatar) => (
                    <div
                        key={avatar.id}
                        style={{
                            border: "1px solid #ddd",
                            borderRadius: "10px",
                            padding: "10px",
                            textAlign: "center",
                            backgroundColor: "#FAFAFA",
                        }}
                    >
                        <img
                            src={avatar.view_url || avatar.blob_url}
                            alt="avatar"
                            style={{
                                width: "100%",
                                height: "120px",
                                objectFit: "cover",
                                borderRadius: "8px",
                            }}
                        />
                        <p
                            title={avatar.file_name}
                            style={{
                                margin: "10px 0",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                        >
                            {avatar.file_name}
                        </p>
                        <button
                            onClick={() => toggleActive(avatar.id, avatar.is_active)}
                            disabled={statusToggle.isBusy}
                            style={{
                                padding: "5px 10px",
                                borderRadius: "5px",
                                backgroundColor: avatar.is_active ? "#28a745" : "#dc3545",
                                color: "#fff",
                                border: "none",
                                cursor: statusToggle.isBusy ? "not-allowed" : "pointer",
                                opacity: statusToggle.isBusy ? 0.55 : 1,
                            }}
                        >
                            {statusToggle.isPending(avatar.id)
                                ? "Saving..."
                                : (avatar.is_active ? "Active" : "Inactive")}
                        </button>
                        {/*
                          Deliberately quieter than the toggle beside it. Delete
                          is the irreversible one, so it does not compete for the
                          eye — a red block button next to a green one invites a
                          mis-tap on the destructive half.
                        */}
                        <button
                            onClick={() => deleteAvatar(avatar)}
                            style={{
                                marginLeft: "8px",
                                padding: "5px 10px",
                                borderRadius: "5px",
                                backgroundColor: "transparent",
                                color: "#dc3545",
                                border: "1px solid #dc3545",
                                cursor: "pointer",
                            }}
                        >
                            Delete
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ProfileSettings;


